import {type FormEvent, forwardRef, useCallback, useImperativeHandle, useState} from 'react'
import {Trans, useTranslation} from 'react-i18next'
import {useFirebaseAuth} from '@goschool/react-firebase'
import {useSignInContext} from './SignInContext'
import type {CredentialsSignInHandle} from './CredentialsSignIn'
import {AuthErrorCodes, createUserWithEmailAndPassword, updateProfile} from 'firebase/auth'
import {FirebaseError} from 'firebase/app'
import {Button, DialogActions, FormHelperText, Stack, TextField} from '@mui/material'
import {useAuthenticationContext} from './AuthenticationContext'
import {NoWrapButton, NoWrapFormHelperText} from '@goschool/mui'

export const CredentialsSignUp = forwardRef<CredentialsSignInHandle>(
  function CredentialsSignIn(props, ref) {
    const {t} = useTranslation()
    const {auth} = useFirebaseAuth()

    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [displayName, setDisplayName] = useState('')
    const [passwordCheck, setPasswordCheck] = useState('')

    const [emailError, setEmailError] = useState<string | null>(null)
    const [passwordError, setPasswordError] = useState<string | null>(null)
    const [displayNameError, setDisplayNameError] = useState<string | null>(null)
    const [passwordCheckError, setPasswordCheckError] = useState<string | null>(null)

    const {state, setState, handleError, handleSuccess, resetError} = useSignInContext()

    const resetFields = useCallback(() => {
      setEmail('')
      setDisplayName('')
      setPassword('')
      setPasswordCheck('')
    }, [])

    const resetErrors = useCallback(() => {
      setEmailError(null)
      setPasswordError(null)
      setDisplayNameError(null)
      setPasswordCheckError(null)
      resetError()
    }, [resetError])

    const reset = useCallback(() => {
      resetFields()
      resetErrors()
    }, [resetErrors, resetFields])


    const {enabledMethods} = useAuthenticationContext()

    const doSubmit = useCallback(
      async (e: FormEvent) => {
        e.preventDefault()
        if (!['idle', 'failed'].includes(state)) {
          throw new Error(`Cannot submit while in state ${state}`)
        }
        if (displayName.length === 0) {
          setDisplayNameError('Missing name')
          return
        }

        setState('pending')
        resetErrors()
        try {
          const credential = await createUserWithEmailAndPassword(auth, email, password)
          await updateProfile(credential.user, {displayName})
          await handleSuccess(credential)
          resetFields()
          setState('idle')
        } catch (e) {
          if (e instanceof FirebaseError) {
            if (e.code === 'auth/invalid-email') {
              setEmailError('Invalid email')
            } else if (e.code === AuthErrorCodes.EMAIL_EXISTS) {
              setEmailError(AuthErrorCodes.EMAIL_EXISTS)
            } else if (e.code === 'auth/missing-password') {
              setPasswordError('Missing password')
            } else {
              handleError(e, 'credentials')
            }
          } else {
            handleError(e, 'credentials')
          }
        }
      }, [auth, displayName, email, handleError, handleSuccess, password, resetErrors, resetFields, setState, state])

    useImperativeHandle(ref, () => ({
      submit: doSubmit,
      reset: reset
    }), [doSubmit, reset])

    if (!enabledMethods.includes('credentials')) {
      return null
    }

    return <>
      <FormHelperText><Trans i18nKey="auth:signUp.form.useCredentials"/></FormHelperText>
      <TextField
        size="small" fullWidth={true}
        label={t('auth:signUp.form.email')} value={email}
        error={emailError != null} helperText={emailError}
        onChange={e => setEmail(e.target.value)}/>

      <TextField
        size="small" fullWidth={true}
        label={t('auth:signUp.form.displayName')} value={displayName}
        error={displayNameError != null} helperText={displayNameError}
        onChange={e => setDisplayName(e.target.value)}/>


      <TextField
        size="small" fullWidth={true} type="password"
        error={passwordError != null} helperText={passwordError}
        label={t('auth:signUp.form.password')} value={password}
        onChange={e => setPassword(e.target.value)}/>

      <TextField
        size="small" fullWidth={true} type="password"
        error={passwordCheckError != null} helperText={passwordCheckError}
        label={t('auth:signUp.form.passwordCheck')} value={passwordCheck}
        onChange={e => setPasswordCheck(e.target.value)}/>

    </>
  }
)

export function CredentialsSignUpActions() {
  const {state} = useSignInContext()
  const {enabledMethods, signUp} = useAuthenticationContext()

  if (!enabledMethods.includes('credentials')) {
    return null
  }

  return <DialogActions>
    <Stack direction="row" justifyContent="flex-start" alignItems="baseline" flexWrap="wrap">
      <NoWrapFormHelperText><Trans i18nKey="auth:signUp.form.registered"/></NoWrapFormHelperText>
      <NoWrapButton variant="text" sx={{textTransform: 'inherit'}} onClick={signUp} size="small"><Trans
        i18nKey="auth:signUp.form.signIn"/></NoWrapButton>
    </Stack>
    <Button sx={{flexShrink: 0}} type="submit" variant="contained" color="primary"
      loading={state === 'pending'} autoFocus={true}>
      <Trans i18nKey="auth:signUp.form.submit"/>
    </Button>
  </DialogActions>
}
